import type { ApiResponse } from '#shared/types/api'
import type { BookingTotalCalculation, CouponValidationResponse } from '#shared/types/coupons'
import type { Booking, BookingItem, BookingParticipant, Camp } from '#shared/types/custom.types'
import type { FormResponseGroup } from '#shared/types/forms'
import { useAdminFiltersStore } from '~/stores/adminFilters'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

interface CreateBookingPayload {
  camp_id: string
  ticket_items: {
    ticket_type_id: string
    quantity: number
    day_tickets?: {
      date: string
      quantity: number
    }[]
  }[]
  participants: {
    first_name: string
    last_name: string
    date_of_birth: string
    gender?: string
    medical_info?: string
    allergies?: string
    emergency_contact_name?: string
    emergency_contact_phone?: string
    form_responses?: FormResponseGroup[]
    attendance_date?: string
    user_attendee_id?: string
  }[]
  coupon_code?: string
  notes?: string
}

export interface BookingWithDetails extends Booking {
  camp?: Camp
  vzw_camps?: Camp
  booking_items?: BookingItem[]
  participants?: BookingParticipant[]
  vzw_booking_participants?: BookingParticipant[]
}

interface GetBookingsParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  payment_status?: string
  camp_id?: string
  user_id?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  detail?: string
}

export function useBookings() {
  /**
   * Create a new booking with atomic transaction
   */
  const createBooking = async (payload: CreateBookingPayload): Promise<ApiResponse<Booking>> => {
    try {
      return await $api('/api/bookings/create', {
        method: 'POST',
        body: payload,
      })
    }
    catch (error) {
      logApiError('/api/bookings/create', error, { payload })
      throw error
    }
  }

  /**
   * Get bookings with filtering and pagination
   */
  const getBookings = async (params: GetBookingsParams = {}): Promise<ApiResponse<BookingWithDetails[]>> => {
    try {
      const adminFilters = useAdminFiltersStore()
      const queryString = buildQueryString({ ...adminFilters.toQueryParams(), ...params })

      return await $api(`/api/bookings?${queryString}`, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError('/api/bookings', error, { params })
      throw error
    }
  }

  /**
   * Get booking by ID
   */
  const getBookingById = async (id: string): Promise<ApiResponse<BookingWithDetails>> => {
    try {
      return await $api(`/api/bookings/${id}`, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError(`/api/bookings/${id}`, error, { id })
      throw error
    }
  }

  /**
   * Get current user's bookings
   */
  const getMyBookings = async (params: Omit<GetBookingsParams, 'user_id'> = {}): Promise<ApiResponse<BookingWithDetails[]>> => {
    try {
      const queryString = buildQueryString(params)

      return await $api(`/api/bookings/my?${queryString}`, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError('/api/bookings/my', error, { params })
      throw error
    }
  }

  /**
   * Cancel a booking
   */
  const cancelBooking = async (id: string, reason?: string): Promise<ApiResponse<Booking>> => {
    try {
      return await $api(`/api/bookings/${id}/cancel`, {
        method: 'POST',
        body: { reason },
      })
    }
    catch (error) {
      logApiError(`/api/bookings/${id}/cancel`, error, { id, reason })
      throw error
    }
  }

  /**
   * Update booking status
   */
  const updateBookingStatus = async (
    id: string,
    status: string,
    notes?: string,
  ): Promise<ApiResponse<Booking>> => {
    try {
      return await $api(`/api/bookings/${id}/status`, {
        method: 'PATCH',
        body: { status, notes },
      })
    }
    catch (error) {
      logApiError(`/api/bookings/${id}/status`, error, { id, status, notes })
      throw error
    }
  }

  /**
   * Update participant information
   */
  const updateParticipant = async (
    bookingId: string,
    participantId: string,
    data: Partial<CreateBookingPayload['participants'][0]>,
  ): Promise<ApiResponse<BookingParticipant>> => {
    try {
      return await $api(`/api/bookings/${bookingId}/participants/${participantId}`, {
        method: 'PATCH',
        body: data,
      })
    }
    catch (error) {
      logApiError(`/api/bookings/${bookingId}/participants/${participantId}`, error, { bookingId, participantId })
      throw error
    }
  }

  /**
   * Validate coupon code
   */
  const validateCoupon = async (code: string, campId: string): Promise<ApiResponse<CouponValidationResponse>> => {
    try {
      return await $api('/api/bookings/validate-coupon', {
        method: 'POST',
        body: { code, camp_id: campId },
      })
    }
    catch (error) {
      logApiError('/api/bookings/validate-coupon', error, { code, campId })
      throw error
    }
  }

  /**
   * Calculate booking total with discounts
   */
  const calculateTotal = async (payload: {
    camp_id: string
    ticket_items: CreateBookingPayload['ticket_items']
    coupon_code?: string
  }): Promise<ApiResponse<BookingTotalCalculation>> => {
    try {
      return await $api('/api/bookings/calculate-total', {
        method: 'POST',
        body: payload,
      })
    }
    catch (error) {
      logApiError('/api/bookings/calculate-total', error, { payload })
      throw error
    }
  }

  /**
   * Add participant to existing booking
   */
  const addParticipant = async (
    bookingId: string,
    participant: CreateBookingPayload['participants'][0],
  ): Promise<ApiResponse<BookingParticipant>> => {
    try {
      return await $api(`/api/bookings/${bookingId}/participants`, {
        method: 'POST',
        body: participant,
      })
    }
    catch (error) {
      logApiError(`/api/bookings/${bookingId}/participants`, error, { bookingId })
      throw error
    }
  }

  /**
   * Remove participant from booking
   */
  const removeParticipant = async (
    bookingId: string,
    participantId: string,
  ): Promise<ApiResponse<void>> => {
    try {
      return await $api(`/api/bookings/${bookingId}/participants/${participantId}`, {
        method: 'DELETE',
      })
    }
    catch (error) {
      logApiError(`/api/bookings/${bookingId}/participants/${participantId}`, error, { bookingId, participantId })
      throw error
    }
  }

  return {
    createBooking,
    getBookings,
    getBookingById,
    getMyBookings,
    cancelBooking,
    updateBookingStatus,
    updateParticipant,
    validateCoupon,
    calculateTotal,
    addParticipant,
    removeParticipant,
  }
}
